import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Box, Typography} from '@mui/material';
import {Trans, useTranslation} from 'react-i18next';
import {useLocation, useNavigate, useSearchParams} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import ArrTooltipWrapper from '../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import ArrToastNotification from '../../common/arr-toast-notification/ArrToastNotification';
import ArrModal from '../../common/arr-modal/ArrModal';
import {ReactComponent as InfoIcon} from '../../../assets/images/info-icon.svg';
import {ArrStepper, Step} from '../../common/arr-stepper/ArrStepper';
import StepWrapper from '../create-new-production/common/step-wrapper/StepWrapper';
import {useMultiStepForm} from '../../../hooks/productions/useMultiStepForm';
import {setReproductionIsFieldsChanged} from '../../../redux/slice/reproductionSlice';
import {RootState} from '../../../redux/store';
import {ROUTE_PRODUCTION_HOME} from '../create-new-production/CreateNewProductionUtils';
import {
  ReProductionFormData,
  DEFAULT_FORM_DATA,
  STEP_KEY_BASIC_INFO,
  STEP_KEY_SELECT_SCOPE,
  STEP_KEY_SELECT_PRODUCTIONS,
  STEP_KEY_PRODUCTION_COMPONENTS,
  STEP_KEY_NUMBERING,
  STEP_KEY_OUTPUT_SETTINGS,
  STEP_KEY_GENERATE,
  STEP_KEY_QC_CONFIRMATION,
  reProductionStepperItems,
  getReProductionStepConfig,
  getReProductionStepKeyFromNumber,
  getReProductionIdFromEditResponse,
  getReProductionTypeFromEditResponse,
  resolveReProductionWizardStatusCode,
  isReProductionCommitWorkflowInProgressState,
  isReProductionCommitWorkflowFailedState,
  QUERY_PARAM_REPRODUCTION_ID,
  ToastType,
  ProductionStatus,
} from './ReProductionUtils';
import ReProductionBasicInfo from './basic-info/ReProductionBasicInfo';
import SelectScope from './select-scope/SelectScope';
import SelectProductions from './select-productions/SelectProductions';
import ReProductionComponents from './production-components/ReProductionComponents';
import type {ImagePlaceholderItem} from './production-components/tiff-pdf-component/TiffPdfComponent';
import ReProductionNumbering from './numbering/ReProductionNumbering';
import ReProductionOutputSettings from './output-settings/OutputSettings';
import ReProductionGenerate from './generate/ReProductionGenerate';
import ReProductionQCConfirmation from './qc-confirmation/ReProductionQCConfirmation';
import {useGetReProductionBasicInfoInEditMode} from '../../../hooks/productions/useReProductions';
import './ReProduction.scss';

// Type for child component ref
type StepChildRef = {
  save: () => void;
  next: () => void;
};

const ReProduction: React.FC = () => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();

  // Extract navigation state
  const navigationState = location.state as {
    securityGroupId?: number;
    productionSetId?: number;
    isProductionSet?: boolean;
    productionId?: number;
  } | null;

  const [showToast, setShowToast] = useState<boolean>(false);
  const [formStatus, setFormStatus] = useState<ToastType>(ToastType.Success);
  const [formMessage, setFormMessage] = useState<string | undefined>('');
  const [selectedSecurityGroupId, setSelectedSecurityGroupId] =
    useState<number>(0);
  const [selectedProductionSetId, setSelectedProductionSetId] =
    useState<number>(0);
  const [, setSteps] = useState<Step[]>(reProductionStepperItems());
  const [formData, setFormData] =
    useState<ReProductionFormData>(DEFAULT_FORM_DATA);

  // Button control state
  const [isSaveDisabled, setIsSaveDisabled] = useState(false);
  const [isNextDisabled, setIsNextDisabled] = useState(true);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [initialStepKey, setInitialStepKey] =
    useState<string>(STEP_KEY_BASIC_INFO);
  // Read-only state derived from API's productionCurrentStatus only.
  // Decoupled from `isSaveDisabled` (which also reflects form validity reported by child steps).
  // Empty string = editable. INPROGRESS / COMPLETED = read-only.
  const [productionStatusCode, setProductionStatusCode] = useState<string>('');
  const [isWizardEditableAfterUncommit, setIsWizardEditableAfterUncommit] =
    useState(false);
  // Tracks the live state code reported by the QC confirmation step.
  // Used to lock the wizard during commit/uncommit in-progress or failed states.
  const [qcStateCode, setQcStateCode] = useState<string>('');
  const wizardStatusFromGenerateRef = useRef<string>('');
  const nativePlaceholderBaselineRef = useRef<ImagePlaceholderItem[]>([]);

  const isFieldsChanged = useSelector(
    (state: RootState) => state.reproduction.isFieldsChanged
  );

  // Ref to track if navigation state has been consumed
  const hasConsumedNavigationState = useRef<boolean>(false);

  const hasSetInitialStep = useRef<boolean>(
    !!(location.state as {justSaved?: boolean} | null)?.justSaved
  );

  // Ref to communicate with child components
  const childRef = useRef<StepChildRef | null>(null);

  const encryptedProductionId =
    searchParams.get(QUERY_PARAM_REPRODUCTION_ID) || '';

  // Step definitions for useMultiStepForm — memoized
  const stepDefinitions = useMemo(
    () =>
      reProductionStepperItems().map((s) => ({
        key: s.key,
        label: s.label,
        tooltipTitle: s.tooltipTitle,
        tooltip: s.tooltip,
      })),
    []
  );

  // Ref mirror of `currentStepKey` so `handleStepChange` can read it without being recreated.
  // Using inline onStepChange made `goToStep` unstable → any effect with `goToStep` in deps looped.
  const currentStepKeyRef = useRef<string>(STEP_KEY_BASIC_INFO);

  const handleStepChange = useCallback((key: string) => {
    setSteps((prev) =>
      prev.map((s) =>
        s.key === key
          ? {...s, state: 'current'}
          : s.key === currentStepKeyRef.current
            ? {...s, state: 'completed'}
            : s
      )
    );
  }, []);

  const {
    currentStepKey,
    steps: multiStepSteps,
    goNext,
    goBack,
    goToStep,
  } = useMultiStepForm({
    stepDefinitions,
    initialStepKey,
    onStepChange: handleStepChange,
  });

  useEffect(() => {
    currentStepKeyRef.current = currentStepKey;
  }, [currentStepKey]);

  // Each step owns dirty tracking for its own form.
  // Reset stale dirty flags when switching steps to avoid false "Leave page?" prompts.
  useEffect(() => {
    dispatch(setReproductionIsFieldsChanged({isFieldsChanged: false}));
  }, [currentStepKey, dispatch]);

  const [, setStepsMovedNext] = useState<Record<string, boolean>>({});
  const {
    data: reProductionEditModeData,
    mutate: getReProductionBasicInfoInEditMode,
  } = useGetReProductionBasicInfoInEditMode();

  // Initialize — clear fields changed flag
  useEffect(() => {
    dispatch(setReproductionIsFieldsChanged({isFieldsChanged: false})); // ← was setIsFieldsChanged
    setShowModal(false);
  }, [dispatch]);

  useEffect(() => {
    if ((location.state as {justSaved?: boolean} | null)?.justSaved) {
      hasSetInitialStep.current = true;
      navigate(location.pathname + location.search, {
        replace: true,
        state: null,
      });
    }
  }, [location.state, location.pathname, location.search, navigate]);

  // Consume navigation state
  useEffect(() => {
    if (encryptedProductionId) {
      return;
    }

    if (hasConsumedNavigationState.current) {
      return;
    }

    if (navigationState?.securityGroupId && navigationState?.productionSetId) {
      setSelectedSecurityGroupId(navigationState.securityGroupId);
      setSelectedProductionSetId(navigationState.productionSetId);
      if (
        Number.isFinite(navigationState.productionId) &&
        Number(navigationState.productionId) > 0
      ) {
        setFormData((prev) => ({
          ...prev,
          productionId: Number(navigationState.productionId),
        }));
      }

      hasConsumedNavigationState.current = true;

      // Clear navigation state
      navigate(location.pathname, {replace: true, state: null});
    } else {
      // No navigation state — redirect back
      navigate(ROUTE_PRODUCTION_HOME, {replace: true});
    }
  }, [navigationState, navigate, location.pathname, encryptedProductionId]);

  useEffect(() => {
    if (encryptedProductionId) {
      getReProductionBasicInfoInEditMode(encryptedProductionId);
    }
  }, [encryptedProductionId, getReProductionBasicInfoInEditMode]);

  useEffect(() => {
    if (!encryptedProductionId || !reProductionEditModeData) return;

    // ── Unwrap once — use for ALL fields ─────────────────────────────────────
    const data =
      (reProductionEditModeData as any)?.data ?? reProductionEditModeData;

    const nextSecurityGroupId = data.securityGroupId || 0;
    const nextProductionSetId = data.parentSetID || 0;
    const nextReProductionId = getReProductionIdFromEditResponse(data);
    const nextReProductionType = getReProductionTypeFromEditResponse(data);

    setSelectedSecurityGroupId((prev) =>
      prev === nextSecurityGroupId ? prev : nextSecurityGroupId
    );
    setSelectedProductionSetId((prev) =>
      prev === nextProductionSetId ? prev : nextProductionSetId
    );
    setFormData((prev) => {
      const updates: Partial<ReProductionFormData> = {};
      if (prev.productionId !== nextReProductionId)
        updates.productionId = nextReProductionId;
      if (prev.reProductionType !== nextReProductionType)
        updates.reProductionType = nextReProductionType;
      return Object.keys(updates).length ? {...prev, ...updates} : prev;
    });

    if (data.currentStep !== undefined && !hasSetInitialStep.current) {
      const stepKey = getReProductionStepKeyFromNumber(data.currentStep);
      setInitialStepKey(stepKey);
      goToStep(stepKey);
      hasSetInitialStep.current = true;
    }

    const resolvedStatusCode = resolveReProductionWizardStatusCode(data);

    if (resolvedStatusCode === '') {
      wizardStatusFromGenerateRef.current = '';
    }

    let mergedStatusCode =
      resolvedStatusCode === ProductionStatus.COMPLETED ||
      wizardStatusFromGenerateRef.current === ProductionStatus.COMPLETED
        ? ProductionStatus.COMPLETED
        : resolvedStatusCode === ProductionStatus.INPROGRESS ||
            wizardStatusFromGenerateRef.current === ProductionStatus.INPROGRESS
          ? ProductionStatus.INPROGRESS
          : resolvedStatusCode;

    if (isWizardEditableAfterUncommit) {
      wizardStatusFromGenerateRef.current = '';
      mergedStatusCode = '';
    }

    // Seed qcStateCode from the API payload so that commit/uncommit in-progress
    // or failed states lock the wizard immediately — even before the QC step mounts.
    const apiStateCode = String(data.productionStateCode ?? '');
    if (
      !isWizardEditableAfterUncommit &&
      (isReProductionCommitWorkflowInProgressState(apiStateCode) ||
        isReProductionCommitWorkflowFailedState(apiStateCode))
    ) {
      setQcStateCode(apiStateCode);
    } else {
      setQcStateCode('');
    }

    setProductionStatusCode(mergedStatusCode);
    setIsSaveDisabled(mergedStatusCode !== '');
  }, [
    encryptedProductionId,
    reProductionEditModeData,
    goToStep,
    isWizardEditableAfterUncommit,
  ]);

  // Navigation handlers
  const handleSave = () => {
    childRef.current?.save();
  };

  const handleNext = () => {
    if (childRef.current) {
      childRef.current?.next();
      setStepsMovedNext((prev) => ({
        ...prev,
        [currentStepKey]: true,
      }));
    }
  };

  // True when a commit or uncommit operation is running or has failed.
  // During these states the wizard must be read-only and only Next is enabled.
  const isCommitInProgressOrFailed =
    !isWizardEditableAfterUncommit &&
    (isReProductionCommitWorkflowInProgressState(qcStateCode) ||
      isReProductionCommitWorkflowFailedState(qcStateCode));

  const isWizardReadOnly =
    !isWizardEditableAfterUncommit &&
    (productionStatusCode === ProductionStatus.COMPLETED ||
      productionStatusCode === ProductionStatus.INPROGRESS ||
      isCommitInProgressOrFailed);

  // Lock Save immediately whenever commit/uncommit is in-progress or failed.
  // Next is enabled for navigation during commit workflow on every step including Generate.
  useEffect(() => {
    if (isCommitInProgressOrFailed) {
      setIsSaveDisabled(true);
      setIsNextDisabled(false);
    }
  }, [isCommitInProgressOrFailed]);

  const handleBack = () => {
    if (isFieldsChanged && !isWizardReadOnly) {
      setShowModal(true);
      return;
    }
    if (isFieldsChanged && isWizardReadOnly) {
      dispatch(setReproductionIsFieldsChanged({isFieldsChanged: false}));
    }
    backNavigation();
  };

  const backNavigation = () => {
    goBack();
  };

  const resetWizardEditableAfterUncommit = useCallback(() => {
    wizardStatusFromGenerateRef.current = '';
    setIsWizardEditableAfterUncommit(true);
    setProductionStatusCode('');
    setQcStateCode(''); // clear commit state so wizard becomes fully editable
    if (encryptedProductionId) {
      getReProductionBasicInfoInEditMode(encryptedProductionId);
    }
  }, [encryptedProductionId, getReProductionBasicInfoInEditMode]);

  const handleWizardStatusChange = useCallback(
    (status: string) => {
      if (isWizardEditableAfterUncommit) {
        if (status === ProductionStatus.INPROGRESS) {
          setIsWizardEditableAfterUncommit(false);
          wizardStatusFromGenerateRef.current = status;
          setProductionStatusCode(status);
          setIsSaveDisabled(true);
          return;
        }
        wizardStatusFromGenerateRef.current = '';
        setProductionStatusCode('');
        return;
      }

      if (!status) {
        wizardStatusFromGenerateRef.current = '';
        setProductionStatusCode('');
        setIsSaveDisabled(false);
        return;
      }
      wizardStatusFromGenerateRef.current = status;
      setProductionStatusCode(status);
      setIsSaveDisabled(true);
    },
    [isWizardEditableAfterUncommit]
  );

  // Receives live commit/uncommit state from QC and Generate steps.
  const handleCommitStateChange = useCallback((stateCode: string) => {
    setQcStateCode(stateCode);
  }, []);

  // Handle child state changes
  const handleChildStateChange = useCallback(
    (state: {saveDisabled: boolean; nextDisabled: boolean}) => {
      if (isWizardEditableAfterUncommit) {
        setIsSaveDisabled(state.saveDisabled);
        setIsNextDisabled(state.nextDisabled);
        return;
      }

      if (isCommitInProgressOrFailed) {
        setIsSaveDisabled(true);
        setIsNextDisabled(false);
        return;
      }

      const isGenerating = productionStatusCode === ProductionStatus.INPROGRESS;
      const isCompleted = productionStatusCode === ProductionStatus.COMPLETED;

      setIsSaveDisabled(
        isGenerating || isCompleted ? true : state.saveDisabled
      );
      setIsNextDisabled(state.nextDisabled);
    },
    [
      productionStatusCode,
      isWizardEditableAfterUncommit,
      isCommitInProgressOrFailed,
    ]
  );

  const childProductionStatusCode = isWizardEditableAfterUncommit
    ? ''
    : isCommitInProgressOrFailed
      ? ProductionStatus.COMPLETED
      : productionStatusCode;

  // Handle form data updates from child components (optional `silent` avoids marking dirty on API hydration)
  const handleFormDataChange = useCallback(
    (updates: Partial<ReProductionFormData>, options?: {silent?: boolean}) => {
      let didChange = false;
      setFormData((prev) => {
        const next = {...prev, ...updates};
        didChange = JSON.stringify(prev) !== JSON.stringify(next);
        return didChange ? next : prev;
      });
      if (didChange && !options?.silent) {
        dispatch(setReproductionIsFieldsChanged({isFieldsChanged: true}));
      }
    },
    [dispatch]
  );

  const renderStepContent = () => {
    switch (currentStepKey) {
      case STEP_KEY_BASIC_INFO:
        return (
          <ReProductionBasicInfo
            ref={childRef}
            productionId={formData.productionId}
            onFormDataChange={handleFormDataChange}
            getStatus={(isShowToast, status, message) => {
              setShowToast(isShowToast);
              setFormStatus(status);
              setFormMessage(message);
            }}
            goNext={goNext}
            goBack={goBack}
            onStateChange={handleChildStateChange}
            securityGroupId={selectedSecurityGroupId}
            productionSetId={selectedProductionSetId}
            productionStatusCode={childProductionStatusCode}
          />
        );

      case STEP_KEY_SELECT_SCOPE:
        return (
          <SelectScope
            ref={childRef}
            reProductionId={formData.productionId}
            getStatus={(isShowToast, status, message) => {
              setShowToast(isShowToast);
              setFormStatus(status);
              setFormMessage(message);
            }}
            goNext={goNext}
            goBack={goBack}
            onStateChange={handleChildStateChange}
            securityGroupId={selectedSecurityGroupId}
            productionStatusCode={childProductionStatusCode}
          />
        );

      case STEP_KEY_SELECT_PRODUCTIONS:
        return (
          <SelectProductions
            ref={childRef}
            reProductionId={formData.productionId}
            securityGroupId={selectedSecurityGroupId}
            productionSetId={selectedProductionSetId}
            reProductionType={formData.reProductionType}
            formData={formData}
            onFormDataChange={handleFormDataChange}
            getStatus={(isShowToast, status, message) => {
              setShowToast(isShowToast);
              setFormStatus(status);
              setFormMessage(message);
            }}
            goNext={goNext}
            goBack={goBack}
            onStateChange={handleChildStateChange}
            productionStatusCode={childProductionStatusCode}
          />
        );

      case STEP_KEY_PRODUCTION_COMPONENTS:
        return (
          <ReProductionComponents
            ref={childRef}
            formData={formData}
            productionId={formData.productionId}
            nativePlaceholderBaselineRef={nativePlaceholderBaselineRef}
            getStatus={(isShowToast, status, message) => {
              setShowToast(isShowToast);
              setFormStatus(status);
              setFormMessage(message);
            }}
            goNext={goNext}
            goBack={goBack}
            onStateChange={handleChildStateChange}
            productionStatusCode={childProductionStatusCode}
          />
        );

      case STEP_KEY_NUMBERING:
        return (
          <ReProductionNumbering
            ref={childRef}
            formData={formData}
            onFormDataChange={handleFormDataChange}
            getStatus={(isShowToast, status, message) => {
              setShowToast(isShowToast);
              setFormStatus(status);
              setFormMessage(message);
            }}
            goNext={goNext}
            goBack={goBack}
            onStateChange={handleChildStateChange}
            securityGroupId={selectedSecurityGroupId}
            reProductionId={formData.productionId}
            productionStatusCode={childProductionStatusCode}
          />
        );

      case STEP_KEY_OUTPUT_SETTINGS:
        return (
          <ReProductionOutputSettings
            ref={childRef}
            productionId={formData.productionId}
            isProductionSet
            getStatus={(isShowToast, status, message) => {
              setShowToast(isShowToast);
              setFormStatus(status);
              setFormMessage(message);
            }}
            goNext={goNext}
            goBack={goBack}
            onStateChange={handleChildStateChange}
            productionStatusCode={childProductionStatusCode}
          />
        );

      case STEP_KEY_GENERATE:
        return (
          <ReProductionGenerate
            ref={childRef}
            formData={formData}
            onFormDataChange={handleFormDataChange}
            getStatus={(isShowToast, status, message) => {
              setShowToast(isShowToast);
              setFormStatus(status);
              setFormMessage(message);
            }}
            goNext={goNext}
            goBack={goBack}
            onStateChange={handleChildStateChange}
            onWizardStatusChange={handleWizardStatusChange}
            onCommitUncommitStateChange={handleCommitStateChange}
            isWizardEditableAfterUncommit={isWizardEditableAfterUncommit}
            securityGroupId={selectedSecurityGroupId}
            productionSetId={selectedProductionSetId}
            productionStatusCode={childProductionStatusCode}
          />
        );

      case STEP_KEY_QC_CONFIRMATION:
        return (
          <ReProductionQCConfirmation
            ref={childRef}
            productionId={formData.productionId}
            isProductionSet
            productionName={formData.name}
            securityGroupId={selectedSecurityGroupId}
            productionSetId={selectedProductionSetId}
            formData={formData}
            getStatus={(isShowToast, status, message) => {
              setShowToast(isShowToast);
              setFormStatus(status);
              setFormMessage(message);
            }}
            goBack={goBack}
            productionStatusCode={childProductionStatusCode}
            onWizardEditableAfterUncommit={resetWizardEditableAfterUncommit}
            onCommitUncommitStateChange={handleCommitStateChange}
          />
        );

      default:
        return null;
    }
  };

  return (
    <>
      <Box className="re-production-content">
        <Box className="re-production-container">
          <Box className="re-production-header">
            <Box className="re-production-title">
              <Typography className="typo-text headline3_size re-production-title-text">
                {t('reProduction.headerTitle')}
              </Typography>
              <ArrTooltipWrapper
                title={t('reProduction.headerTitle')}
                trigger={<InfoIcon fontSize="medium" className="info-icon" />}
              >
                <Trans
                  i18nKey="reProduction.headerTooltip.content"
                  defaults="Re-produce documents that have already been included in a prior production. You can change the format, switch between producing a document or a placeholder, or add/remove redactions and branding."
                  components={{
                    br: <br />,
                    strong: <strong />,
                    p: <p />,
                    ul: <ul />,
                    li: <li />,
                  }}
                />
              </ArrTooltipWrapper>
            </Box>
            <Box className="re-production-stepper">
              <ArrStepper
                steps={multiStepSteps}
                currentStepKey={currentStepKey}
                allowNonLinear={false}
                onStepChange={goToStep}
                dataTestId={'arr-stepper-reproduction'}
              />
            </Box>
          </Box>

          <StepWrapper
            steps={multiStepSteps}
            currentStepKey={currentStepKey}
            isSaveDisabled={isSaveDisabled}
            isNextDisabled={isNextDisabled}
            isBackDisabled={false}
            onSave={handleSave}
            onNext={handleNext}
            onBack={handleBack}
            onStepChange={goToStep}
            {...getReProductionStepConfig(currentStepKey)}
          >
            {renderStepContent()}
          </StepWrapper>
        </Box>
      </Box>

      {showToast && (
        <ArrToastNotification
          message={formMessage || ''}
          status={[formStatus]}
          setErrorIconAsAlert={formStatus !== ToastType.Success}
          onClose={() => setShowToast(false)}
        />
      )}

      {showModal && (
        <ArrModal
          showModal={showModal}
          ariaLabel={t(
            'productionsAndExports.createNewProduction.common.unsavedChangesModal.title'
          )}
          title={t(
            'productionsAndExports.createNewProduction.common.unsavedChangesModal.title'
          )}
          ariaDescription={t(
            'productionsAndExports.createNewProduction.common.unsavedChangesModal.description'
          )}
          description={t(
            'productionsAndExports.createNewProduction.common.unsavedChangesModal.description'
          )}
          onClose={() => {
            setShowModal(false);
          }}
          autoFocusCloseButton={true}
          headerTitle={t(
            'productionsAndExports.createNewProduction.common.unsavedChangesModal.headerTitle'
          )}
          buttons={[
            {
              id: 'no-button',
              label: t(
                'productionsAndExports.createNewProduction.common.unsavedChangesModal.btnCancel'
              ),
              dataTestId: 'no-button',
              onClick: () => {
                setShowModal(false);
              },
              variant: 'secondary',
              disabled: false,
            },
            {
              id: 'yes-button',
              label: t(
                'productionsAndExports.createNewProduction.common.unsavedChangesModal.btnLeave'
              ),
              dataTestId: 'yes-button',
              onClick: () => {
                setShowModal(false);
                dispatch(
                  setReproductionIsFieldsChanged({isFieldsChanged: false})
                );
                backNavigation();
              },
              variant: 'primary',
              loading: false,
              disabled: false,
            },
          ]}
          dataTestId={'arr-reprod-unsavedChangesModal'}
        />
      )}
    </>
  );
};

export default ReProduction;
